import { useState, useEffect } from 'react';
import type { Category, Location } from '@/types/settings';
import type { SubLedger, SystemYear, AssetLookupText, Supplier, FinancialsSupplierAddress } from '@/types/financials';
import type { AssetYearValue } from '@/types/asset';
import { dataService } from '@/services/data-service';
import { financialsService } from '@/services/financials-graphql-service';

interface FetchState<T> {
  data: T;
  loading: boolean;
  error: boolean;
}

interface UserKeyOptions {
  key1: AssetLookupText[];
  key2: AssetLookupText[];
  key3: AssetLookupText[];
  key4: AssetLookupText[];
  key5: AssetLookupText[];
}

interface AssetEditorDataState {
  categories: FetchState<Category[]>;
  locations: FetchState<Location[]>;
  subLedgers: FetchState<SubLedger[]>;
  systemYears: FetchState<SystemYear[]>;
  suppliers: FetchState<Supplier[]>;
  userKeys: FetchState<UserKeyOptions>;
}

interface YearValuesState extends FetchState<AssetYearValue[]> {}

interface SupplierAddressesState extends FetchState<FinancialsSupplierAddress[]> {}

const initialFetchState = <T>(defaultValue: T): FetchState<T> => ({
  data: defaultValue,
  loading: false,
  error: false,
});

const initialUserKeyOptions: UserKeyOptions = {
  key1: [],
  key2: [],
  key3: [],
  key4: [],
  key5: [],
};

export function useAssetEditorData() {
  const [categories, setCategories] = useState<FetchState<Category[]>>(initialFetchState([]));
  const [locations, setLocations] = useState<FetchState<Location[]>>(initialFetchState([]));
  const [subLedgers, setSubLedgers] = useState<FetchState<SubLedger[]>>(initialFetchState([]));
  const [systemYears, setSystemYears] = useState<FetchState<SystemYear[]>>(initialFetchState([]));
  const [suppliers, setSuppliers] = useState<FetchState<Supplier[]>>(initialFetchState([]));
  const [userKeys, setUserKeys] = useState<FetchState<UserKeyOptions>>(initialFetchState(initialUserKeyOptions));

  useEffect(() => {
    const fetchCategories = async () => {
      setCategories(prev => ({ ...prev, loading: true, error: false }));
      try {
        const data = await dataService.getCategories();
        setCategories({ data, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setCategories({ data: [], loading: false, error: true });
      }
    };

    const fetchLocations = async () => {
      setLocations(prev => ({ ...prev, loading: true, error: false }));
      try {
        const data = await dataService.getLocations();
        setLocations({ data, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch locations:', error);
        setLocations({ data: [], loading: false, error: false });
      }
    };

    const fetchSubLedgers = async () => {
      setSubLedgers(prev => ({ ...prev, loading: true, error: false }));
      try {
        const data = await financialsService.getSubLedgers();
        setSubLedgers({ data, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch sub ledgers:', error);
        setSubLedgers({ data: [], loading: false, error: true });
      }
    };

    const fetchSystemYears = async () => {
      setSystemYears(prev => ({ ...prev, loading: true, error: false }));
      try {
        const data = await dataService.getSystemYears('desc');
        setSystemYears({ data, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch system years:', error);
        setSystemYears({ data: [], loading: false, error: true });
      }
    };

    const fetchUserKeyOptions = async () => {
      setUserKeys(prev => ({ ...prev, loading: true }));
      try {
        const [key1, key2, key3, key4, key5] = await Promise.all([
          dataService.getAssetLookupText('ASSORT1'),
          dataService.getAssetLookupText('ASSORT2'),
          dataService.getAssetLookupText('ASSORT3'),
          dataService.getAssetLookupText('ASSORT4'),
          dataService.getAssetLookupText('ASSORT5'),
        ]);
        setUserKeys({ data: { key1, key2, key3, key4, key5 }, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch user key options:', error);
        setUserKeys({ data: initialUserKeyOptions, loading: false, error: false });
      }
    };

    const fetchSuppliers = async () => {
      setSuppliers(prev => ({ ...prev, loading: true, error: false }));
      try {
        const data = await dataService.getSuppliers();
        setSuppliers({ data, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
        setSuppliers({ data: [], loading: false, error: true });
      }
    };

    fetchCategories();
    fetchLocations();
    fetchSubLedgers();
    fetchSystemYears();
    fetchUserKeyOptions();
    fetchSuppliers();
  }, []);

  return {
    categories,
    locations,
    subLedgers,
    systemYears,
    suppliers,
    userKeys,
  };
}

export function useYearValues(assetId: string | undefined, activeTab: string, isCreateMode: boolean) {
  const [state, setState] = useState<YearValuesState>(initialFetchState([]));

  useEffect(() => {
    const fetchYearValues = async () => {
      if (activeTab !== 'values' || isCreateMode || !assetId) {
        return;
      }
      
      setState(prev => ({ ...prev, loading: true, error: false }));
      try {
        const data = await dataService.getAssetYearValues(assetId);
        setState({ data, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch year values:', error);
        setState({ data: [], loading: false, error: true });
      }
    };

    fetchYearValues();
  }, [activeTab, assetId, isCreateMode]);

  return state;
}

export function useSupplierAddresses(supplierId: string | undefined) {
  const [state, setState] = useState<SupplierAddressesState>(initialFetchState([]));

  useEffect(() => {
    const fetchSupplierAddresses = async () => {
      if (!supplierId) {
        setState({ data: [], loading: false, error: false });
        return;
      }

      setState(prev => ({ ...prev, loading: true }));
      try {
        const addresses = await financialsService.getSupplierAddresses(supplierId);
        setState({ data: addresses, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch supplier addresses:', error);
        setState({ data: [], loading: false, error: false });
      }
    };

    fetchSupplierAddresses();
  }, [supplierId]);

  return state;
}
